/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.requestId = 0;
      this.renderedQuery = "";
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = this.querySelector('[name="filter.p.product_type"]');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', this.handleInput.bind(this));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
        return;
      }

      // An exact SKU is a pro re-ordering, not a search. Route straight to the
      // variant instead of rendering a one-row results page.
      const skuMatch = this.querySelector('[data-bd-sku-match]');
      if (this.renderedQuery === this.getQuery() && skuMatch && skuMatch.dataset.bdSkuMatch) {
        evt.preventDefault();
        window.location.href = skuMatch.dataset.bdSkuMatch;
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.requestId += 1;
      if (this.abortController) this.abortController.abort();
      clearTimeout(this.inputTimer);
      this.results.replaceChildren();
      this.removeAttribute('results');
      this.renderedQuery = '';
      this.inputTimer = setTimeout(() => this.getResults(this.getQuery()), 180);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true' && this.renderedQuery === searchTerm) {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '.predictive-search__item[aria-selected="true"]'
      );
      const allResults = [...this.querySelectorAll('.predictive-search__item')].filter(item => item.getClientRects().length && item.querySelector('.js-search-link'));
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = allResults[(allResults.indexOf(selectedResult) - 1 + allResults.length) % allResults.length];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = allResults[(allResults.indexOf(selectedResult) + 1) % allResults.length];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */

    async recoverFlooringResults(section, searchParams, searchTerm, signal) {
      const rows = [...section.querySelectorAll('[data-search-title]')];
      const accessory = /mold(?:ing)?|mould(?:ing)?|trim|adhesive|underlay|accessor|tool|cleaner|grout|stair|vent|transition/i;
      const tokens = searchTerm.toLowerCase().trim().split(/\s+/).filter(token => !/^collections?$/.test(token));
      const matches = row => tokens.every(token => (
        `${row.dataset.searchTitle} ${row.dataset.searchHandle || ''} ${row.dataset.searchVariant || ''} ${row.dataset.searchSku || ''}`
      ).toLowerCase().includes(token));
      if (!rows.length || accessory.test(searchTerm)
        || rows.some(row => Number(row.dataset.searchPriority) < 2 && matches(row))) return section;

      const params = new URLSearchParams(searchParams);
      params.delete('section_id');
      params.set('resources[type]', 'product');
      const response = await fetch(`${theme.routes.predictiveSearch}.json?${params}`, {signal});
      if (!response.ok) return section;
      const payload = await response.json();
      const existing = new Set(rows.map(row => row.dataset.familyId));
      const candidates = (payload.resources?.results?.products || []).filter(product =>
        !existing.has(String(product.id)) && !accessory.test(product.type || '')
        && /floor|hardwood|laminate|vinyl|tile|decking/i.test(product.type || '')
      ).slice(0, 3);
      const recovered = await Promise.allSettled(candidates.map(async product => {
        const productParams = new URLSearchParams(searchParams);
        productParams.set('q', `"${product.title.replace(/"/g, '')}"`);
        productParams.set('resources[type]', 'product');
        productParams.set('resources[options][fields]', 'title');
        const result = await fetch(`${theme.routes.predictiveSearch}?${productParams}`, {signal});
        if (!result.ok) return [];
        const template = document.createElement('template');
        template.innerHTML = await result.text();
        return [...template.content.querySelectorAll('[data-search-title]')]
          .filter(row => row.dataset.familyId === String(product.id) && matches(row));
      }));
      if (signal.aborted) return section;
      const list = rows[0].parentElement;
      const seen = new Set(rows.map(row => row.id));
      recovered.forEach(result => {
        if (result.status !== 'fulfilled') return;
        result.value.forEach(row => {
          if (!seen.has(row.id)) {
            seen.add(row.id);
            list.append(row);
          }
        });
      });
      return section;
    }

    async getResults(searchTerm) {
      if (!searchTerm) return;
      const requestId = ++this.requestId;
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      this.setLiveRegionLoadingState();

      let searchFields = 'title,product_type,variants.title';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';


      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(searchTerm)}`;
      }

      searchParams += `&${encodeURIComponent('resources[limit]')}=${10}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += '&section_id=predictive-search';

      const queryKey = searchParams.replace(' ', '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey], searchTerm);
        return;
      }

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`, {signal: this.abortController.signal});
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        let resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        const tokens = searchTerm.toLocaleLowerCase().trim().split(/\s+/).filter((token, index, all) => all.length === 1 || !/^collections?$/.test(token));
        const hasLiteralMatch = section => Array.from(section.querySelectorAll('[data-search-title]')).some(row => {
          const text = `${row.dataset.searchTitle} ${row.dataset.searchHandle || ''} ${row.dataset.searchVariant || ''} ${row.dataset.searchSku || ''}`.toLocaleLowerCase();
          return tokens.every(token => text.includes(token));
        });
        if (requestId !== this.requestId || searchTerm !== this.getQuery()) return;
        if (/^[\p{L}\p{N}][\p{L}\p{N}\s'-]{2,}$/u.test(searchTerm) && !hasLiteralMatch(resultsEl)) {
          const exactParams = new URLSearchParams(searchParams);
          const phrase = `"${searchTerm}"`;
          exactParams.set('q', this.productTypeInput?.value
            ? `product_type:${this.productTypeInput.value} AND ${phrase}` : phrase);
          try {
            const exactResponse = await fetch(`${theme.routes.predictiveSearch}?${exactParams}`, {signal: this.abortController.signal});
            if (exactResponse.ok) {
              const exactTemplate = document.createElement('template');
              exactTemplate.innerHTML = await exactResponse.text();
              const exactSection = exactTemplate.content.querySelector('#shopify-section-predictive-search');
              if (exactSection && hasLiteralMatch(exactSection)) resultsEl = exactSection;
            }
          } catch (error) {
            if (error.name === 'AbortError') throw error;
          }
        }
        try {
          resultsEl = await this.recoverFlooringResults(resultsEl, searchParams, searchTerm, this.abortController.signal);
        } catch (error) {
          if (error.name === 'AbortError') throw error;
        }
        if (requestId !== this.requestId || searchTerm !== this.getQuery()) return;
        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        this.cachedResults[queryKey] = resultsMarkup;
        if (requestId !== this.requestId || searchTerm !== this.getQuery()) return;
        this.renderResults(resultsMarkup, searchTerm);
      } catch (error) {
        if (error.name === 'AbortError' || requestId !== this.requestId) return;
        this.close();
      }
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup, searchTerm) {
      if (searchTerm !== this.getQuery()) return;
      this.renderedQuery = searchTerm;
      this.results.innerHTML = resultsMarkup;
      const normalize = value => value.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
      const query = normalize(searchTerm);
      const tokens = query.split(' ').filter(Boolean).filter((token, index, all) => all.length === 1 || !/^collections?$/.test(token));
      const rows = Array.from(this.results.querySelectorAll('[data-search-title]'));
      const ranked = rows.map((row, index) => {
        const title = normalize(row.dataset.searchTitle);
        const sku = normalize(row.dataset.searchSku || '');
        const haystack = `${title} ${normalize(row.dataset.searchHandle || '')} ${normalize(row.dataset.searchVariant || '')} ${sku}`;
        const matches = tokens.every(token => haystack.includes(token));
        const score = sku && sku === query ? 1000 : title === query ? 500 : title.startsWith(query) ? 400 : title.includes(query) ? 300 : matches ? 200 : 0;
        const priority = Number(row.dataset.searchPriority ?? 2);
        return {row, index, matches, score: score + (2 - priority) * 2000};
      });
      const hasMatches = ranked.some(item => item.matches);
      const limit = Math.max(1, Math.min(Number(theme.settings.pSearchLimit) || 4, 10));
      const ordered = ranked.filter(item => !hasMatches || item.matches).sort((a,b) => b.score - a.score || a.index - b.index);
      const visible = ordered.slice(0, limit);
      const families = this.results.querySelector('[data-search-families]');
      if (families) {
        const seen = new Set();
        ordered.forEach(({row}) => {
          if (seen.has(row.dataset.familyId) || seen.size >= 3) return;
          seen.add(row.dataset.familyId);
          const family = row.cloneNode(true);
          family.id = `${row.id}-family`;
          [...family.attributes].filter(attr => attr.name.startsWith('data-search-')).forEach(attr => family.removeAttribute(attr.name));
          family.querySelector('a').href = row.dataset.familyUrl;
          family.querySelectorAll('.predictive-result__variant-title, .predictive-result__sku, .predictive-result__sub-title').forEach(el => el.remove());
          const image = family.querySelector('img');
          if (image && row.dataset.familyImage) {
            image.src = row.dataset.familyImage;
            image.removeAttribute('srcset');
            image.alt = row.dataset.searchTitle;
          } else if (image) {
            image.closest('.predictive-result__media').remove();
          }
          families.append(family);
        });
        families.closest('.bd-search-families').hidden = !seen.size;
      }
      const list = rows[0]?.parentElement;
      if (list) {
        rows.forEach(row => row.remove());
        const submit = list.querySelector('.js-submit');
        visible.forEach(item => list.insertBefore(item.row, submit));
      }
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      this.requestId += 1;
      clearTimeout(this.inputTimer);
      if (this.abortController) this.abortController.abort();
      // If reset button was clicked, empty the input field.
      if (evt && this.resetBtn.contains(evt.target)) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
